import React from 'react';
import Icons from '@/const/icons';
import { useRouter } from 'next/router';
import { Breadcrumb } from '@/components/common';
import { useClientCaseDetails } from '@/hooks/queries/useClientCaseDetails';
import { ModalCaseExpires } from '@/components/dashboard';
import { ActionRequiredCard } from '@/components/dashboard/cases/case-details/ActionRequiredCard';
import { CaseProgressSection } from '@/components/dashboard/cases/case-details/CaseProgressSection';
import { DocumentationCard } from '@/components/dashboard/cases/case-details/DocumentationCard';
import { CaseOverviewModal } from '@/components/modals';
import { VerificationReminderModal } from '@/components/modals/VerificationReminderModal';
import AppRoutes from '@/const/routes';
import { useUser } from '@/hooks';
import useModal from '@/hooks/useModal';

export const DashboardCaseDetailsScreen: React.FC = () => {
	const router = useRouter();
	const { id, type } = router.query;
	const pathname = router.pathname;
	const { user } = useUser();
	const {
		Modal: ModalCaseOverview,
		show: showCaseOverview,
		hide: hideCaseOverview,
	} = useModal();

	const {
		actionRequired,
		refetchActionRequired: refecthActionRequired,
		caseApplications,
		refetchCase,
		filesApplications,
		refetchDocuments,
		isDocumentsError,
		clientApproveStatus,
		clientApproveEmail,
	} = useClientCaseDetails(id);

	return (
		<div className="flex flex-col gap-6">
			{caseApplications && (
				<Breadcrumb
					variant="compact"
					paths={[
						{
							name: 'My Cases',
							current: false,
							href: AppRoutes.CLIENTAPP_MY_CASES,
							icon: Icons.briefcase,
						},
						{
							name: caseApplications.data.title || '',
							current: true,
							href: `${AppRoutes.CLIENTAPP_MY_CASES}/${caseApplications.data.id}`,
						},
					]}
				/>
			)}
			<div className="grid grid-cols-1 gap-6 lg:h-[calc(100dvh-180px)] lg:min-h-[420px] lg:grid-cols-[minmax(0,1fr)_338px] lg:grid-rows-[minmax(0,1fr)]">
				<ActionRequiredCard
					stepName={caseApplications?.data.step_name}
					numericalOrder={caseApplications?.data.numerical_order}
					caseName={caseApplications?.data.title || ''}
					applicationId={caseApplications?.data.application_id}
					status={caseApplications?.data.progress_status}
					actionRequired={actionRequired?.data}
					refetchCase={refetchCase}
					refetchActionRequired={refecthActionRequired}
					refetchDocuments={refetchDocuments}
					clientApproveEmail={clientApproveEmail}
					onViewCaseOverview={showCaseOverview}
					className="lg:h-full lg:min-h-0"
					progress={
						<CaseProgressSection
							caseApplications={caseApplications?.data}
							clientApproveStatus={clientApproveStatus?.data}
						/>
					}
				/>
				<div className="lg:relative lg:min-h-80">
					<DocumentationCard
						stepName={caseApplications?.data.step_name}
						applicationId={caseApplications?.data.application_id}
						status={caseApplications?.data.progress_status}
						filesApplications={filesApplications?.data}
						refetch={refetchDocuments}
						isError={isDocumentsError}
						onRetry={refetchDocuments}
						caseName={caseApplications?.data.title}
						clientApproveEmail={clientApproveEmail}
						isClientView={pathname.includes(AppRoutes.CLIENTAPP)}
					/>
				</div>
			</div>
			{caseApplications && (
				<ModalCaseOverview classNameContainerChildren="!max-h-full lg:!max-h-[80vh]">
					<CaseOverviewModal
						caseApplications={caseApplications.data}
						hideConfirmAccount={hideCaseOverview}
					/>
				</ModalCaseOverview>
			)}
			{pathname.includes(AppRoutes.CLIENTAPP) && (
				<ModalCaseExpires
					action={type as string}
					status={caseApplications?.data.progress_status}
				/>
			)}
			<VerificationReminderModal user={user} />
		</div>
	);
};
